/**
 * @file AmendmentApprovalReview.jsx
 * @module ui/src/components/admin/AmendmentApprovalReview
 * @description
 *   Renders the CA-2 delta-first review of a proposed plan amendment, keeping the
 *   current and proposed versions visually explicit while separating governance
 *   approval timing from economic applicability.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #1, #2, #25
 *   Temporal invariants : INV-1, INV-3, INV-4
 *   Design principles : DP-1, DP-2, DP-8, DP-9, DP-10, DP-13, DP-14
 *   Cross-cutting contracts : XC-2, XC-6, XC-7
 *
 * @domain-rules
 *   - CA-2 approves the complete amended document as one governance object rather than approving individual changed sections.
 *   - The current approved version and proposed draft version are always shown together because reviewers must understand the exact version transition.
 *   - Economic effective date comes from the server and governs event attribution; approval timestamp does not determine economic applicability.
 *   - Changed values are rendered by PlanVersionDeltaSummary and version comparison by PlanVersionCompare so delta semantics are not reimplemented locally.
 *   - Projected payout displays distinguish available, unavailable, and partial states, preserving calculated zero as a valid value.
 *   - Approval eligibility is server-authored and rendered by ApprovalEligibilityGate; this component never computes SoD or scope coverage.
 *   - Approval remains server-authoritative and onApprove fires only after an HTTP 200 response.
 *
 * @session-migration-flags
 *   - plan.effective_from / plan.effective_to — pending m_tc_01 (Neon FROZEN)
 *   - amendmentEffectiveDate server domain addition — consumed from approval-review payload when endpoint is built
 *   - GET /api/v1/plans/{id}/amendments/{aid}/approval-review — S777 CONFIRMED NOT BUILT — OI-S774-AMD-APPROVAL-REVIEW-ENDPOINT open — do NOT wire fetch
 *   - VersionDeltaPayload shape — OI-S774-VERSION-DELTA-PAYLOAD open — shape not defined in any spec
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group CA-2 batch delivery
 */

import PropTypes from 'prop-types';
import {
  useEffect,
  useState,
} from 'react';

import ApprovalEligibilityGate from '../shared/ApprovalEligibilityGate';
import DataFreshnessIndicator from '../shared/DataFreshnessIndicator';
import PlanVersionCompare from '../shared/PlanVersionCompare';
import PlanVersionDeltaSummary from '../shared/PlanVersionDeltaSummary';
import ProvisionalBadge from '../shared/ProvisionalBadge';

/**
 * @typedef {Object} AmendmentApprovalReviewProps
 * @property {number} planId
 * @property {string} amendmentId
 * @property {Object} currentUser
 * @property {Function} onApprove
 * @property {Function} onReturn
 */

/**
 * Adapts ep 22b callerEligibility shape → ApprovalEligibilityGate prop shape.
 * Router ep 22b returns callerEligibility: { eligible: boolean, blockedReason: string|null }
 * Gate component reads: { canApprove: boolean, reasonCode: string|null }
 * NOTE: router field is 'callerEligibility' not 'eligibility' — corrected S808.
 */
function ep22bToGateEligibility(callerEligibility) {
  const eligible      = callerEligibility?.eligible      ?? false;
  const blockedReason = callerEligibility?.blockedReason ?? null;
  const reasonCode =
    blockedReason === 'sod_violation'  ? 'self_submitted'  :
    blockedReason === 'scope_mismatch' ? 'scope_mismatch'  :
    blockedReason ?? null;
  return { canApprove: eligible, reasonCode };
}

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const renderEconomicValue = (impact, fieldName) => {
  // WHY: no economic impact payload means no result exists and must never be represented as a calculated zero.
  if (!impact || impact.payoutState === 'not_available') {
    return 'Not available';
  }

  // WHY: partial projection state remains explicit because constrained coverage cannot be presented as a complete figure.
  if (impact.payoutState === 'partial') {
    const value = impact[fieldName];

    if (value === null || value === undefined) {
      return 'Incomplete / partial';
    }

    return `${currencyFormatter.format(value)} — incomplete / partial`;
  }

  // WHY: an available null is treated defensively as unavailable rather than silently coerced into zero.
  if (impact[fieldName] === null || impact[fieldName] === undefined) {
    return 'Not available';
  }

  // WHY: available numeric values, including exactly zero, are displayed as calculated currency.
  return currencyFormatter.format(impact[fieldName]);
};

/**
 * CA-2 delta-first amendment review.
 *
 * @param {AmendmentApprovalReviewProps} props
 * @returns {JSX.Element}
 */
function AmendmentApprovalReview({
  planId,
  amendmentId,
  onApprove,
  onReturn,
}) {
  // WHY: unchanged document sections are hidden initially so amendment review remains delta-first while preserving full-document inspectability.
  const [unchangedExpanded, setUnchangedExpanded] = useState(false);

  // WHY: economic impact begins expanded because consequence is a primary CA-2 approval question.
  const [impactExpanded, setImpactExpanded] = useState(true);

  // WHY: all amendment, temporal, delta, impact, and eligibility facts come from the server-authoritative approval-review payload.
  const [reviewData, setReviewData] = useState(null);

  // WHY: loading state prevents missing approval data from appearing as a valid empty amendment.
  const [loading, setLoading] = useState(false);

  // WHY: read and mutation failures must remain visible without inventing amendment state.
  const [error, setError] = useState('');

  // WHY: approving blocks duplicate governance mutations until the server responds.
  const [approving, setApproving] = useState(false);

  // WHY: compare mode is presentation-only state that lets CA-2 inspect V[N] against V[N+1] without modifying either document.
  const [compareActive, setCompareActive] = useState(false);

  // WHY: amendment review data must be fetched from the server whenever plan or amendment identity changes.
  useEffect(() => {
    let active = true;

    const loadReview = async () => {
      setLoading(true);
      setError('');

      try {
        // WHY: the approval-review endpoint is the authority for version transition, effective date, delta, impact, and eligibility.
        const response = await fetch(
          `/api/v1/plans/${planId}/amendments/${encodeURIComponent(amendmentId)}/approval-review`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
            },
          },
        );

        // WHY: failed review retrieval cannot safely be substituted with client-derived amendment facts.
        if (!response.ok) {
          throw new Error('Unable to load amendment approval review.');
        }

        const payload = await response.json();

        // WHY: stale asynchronous responses must not update an unmounted or identity-changed review surface.
        if (active) {
          setReviewData(payload);
        }
      } catch (requestError) {
        // WHY: only the currently mounted review should receive fetch failure state.
        if (active) {
          setError(
            requestError.message || 'Unable to load amendment approval review.',
          );
        }
      } finally {
        // WHY: the loading flag is cleared only for the active review instance.
        if (active) {
          setLoading(false);
        }
      }
    };

    loadReview();

    return () => {
      active = false;
    };
  }, [planId, amendmentId]);

  // WHY: C-59 section expansion is a navigation signal only and does not create per-section approval semantics.
  const handleSectionExpand = () => {
    // Intentionally no document mutation: C-59 owns its own section presentation behavior.
  };

  // WHY: unchanged content is optionally inspectable while remaining collapsed by default for delta-first review.
  const handleToggleUnchanged = () => {
    setUnchangedExpanded((previous) => !previous);
  };

  // WHY: economic impact can be collapsed for review ergonomics without changing the underlying projection state.
  const handleToggleImpact = () => {
    setImpactExpanded((previous) => !previous);
  };

  // WHY: approval sends one complete-document governance mutation and invokes the parent only after HTTP 200.
  const handleApprove = async () => {
    if (approving) {
      return;
    }

    setApproving(true);
    setError('');

    try {
      // WHY: amendment approval is a server-side governance decision for the complete amended document.
      const response = await fetch(
        `/api/v1/plans/${planId}/amendments/${encodeURIComponent(amendmentId)}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );

      // WHY: anything other than HTTP 200 leaves the amendment outcome unchanged in the client.
      if (response.status !== 200) {
        throw new Error('Unable to approve this amendment.');
      }

      // WHY: orchestration is notified only after server confirmation, satisfying the no-optimistic-UI contract.
      onApprove();
    } catch (requestError) {
      setError(requestError.message || 'Unable to approve this amendment.');
    } finally {
      // WHY: resetting the progress flag permits retry after failure without asserting a local approval outcome.
      setApproving(false);
    }
  };

  // WHY: return is delegated to C-29 because that component owns the structured return rationale and server mutation.
  const handleReturn = () => {
    onReturn();
  };

  // WHY: initial loading is explicitly represented so a missing review payload cannot be mistaken for an amendment with no changes.
  if (loading && !reviewData) {
    return (
      <section className="modifier-manager-card" aria-busy="true">
        <header className="modifier-manager-header">
          <h2>Amendment approval review</h2>
        </header>
        <div className="modifier-manager-body">
          <div className="skeleton" style={{ height: '50px' }} />
          <div className="skeleton" style={{ height: '120px' }} />
          <div className="skeleton" style={{ height: '90px' }} />
        </div>
      </section>
    );
  }

  // WHY: load failure without usable review data blocks approval inspection and therefore renders the established error state.
  if (error && !reviewData) {
    return (
      <div className="modifier-manager-error-state" role="alert">
        {error}
      </div>
    );
  }

  // WHY: absence of a payload after loading is explicit rather than interpreted as a valid no-change amendment.
  if (!reviewData) {
    return (
      <div className="modifier-manager-empty-state">
        Amendment approval review is not available.
      </div>
    );
  }

  const impact = reviewData.economicImpact || {};
  const unchangedSections = reviewData.unchangedSections || [];

  return (
    <section className="modifier-manager-card">
      <header className="modifier-manager-header">
        <div>
          <h2>Amendment approval review</h2>
          <p>
            <strong>{reviewData.planName}</strong> · {reviewData.planCode}
          </p>

          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
            aria-label={`Version ${reviewData.currentVersion} to version ${reviewData.draftVersion}`}
          >
            <span className="modifier-manager-badge modifier-manager-badge--complete">
              V{reviewData.currentVersion} · Current approved
            </span>
            <span aria-hidden="true">→</span>
            <span className="modifier-manager-badge modifier-manager-badge--incomplete">
              V{reviewData.draftVersion} · Proposed
            </span>
          </div>

          <p>
            <strong>Economic effective date:</strong>{' '}
            {reviewData.amendmentEffectiveDate || 'Not specified'}
          </p>

          <p className="modifier-manager-info">
            Approval date does not determine when this amendment governs economic events.
          </p>

          <p>
            <strong>Amendment reason:</strong>{' '}
            {reviewData.amendmentReason || 'Not provided'}
          </p>
        </div>
      </header>

      <div className="modifier-manager-body">
        <section aria-labelledby="amendment-what-changed">
          <h3 id="amendment-what-changed">What changed</h3>

          <PlanVersionDeltaSummary
            delta={reviewData.delta}
            mode="approval"
            onSectionExpand={handleSectionExpand}
          />
        </section>

        <section aria-labelledby="amendment-unchanged-sections">
          <h3 id="amendment-unchanged-sections">
            Unchanged — expand to inspect.
          </h3>

          <button
            type="button"
            className="text-button"
            aria-expanded={unchangedExpanded}
            aria-controls="amendment-unchanged-list"
            onClick={handleToggleUnchanged}
          >
            {unchangedExpanded
              ? 'Hide unchanged sections'
              : `Show unchanged sections (${unchangedSections.length})`}
          </button>

          {/* WHY: unchanged sections remain collapsed by default but are available because CA-2 approves the complete amended document. */}
          {unchangedExpanded && (
            <div id="amendment-unchanged-list">
              {/* WHY: an empty unchanged list is stated explicitly rather than represented as a blank review region. */}
              {unchangedSections.length === 0 ? (
                <p>No unchanged sections were reported.</p>
              ) : (
                // WHY: server-provided section names are rendered directly without deriving document semantics in this component.
                <ul>
                  {unchangedSections.map((sectionName) => (
                    // WHY: each unchanged section is communicated in text for full-document inspectability.
                    <li key={sectionName}>{sectionName}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        <section aria-labelledby="amendment-economic-impact">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <h3 id="amendment-economic-impact">Economic impact</h3>

            <button
              type="button"
              className="text-button"
              aria-expanded={impactExpanded}
              aria-controls="amendment-impact-content"
              onClick={handleToggleImpact}
            >
              {impactExpanded ? 'Hide economic impact' : 'Show economic impact'}
            </button>
          </div>

          {/* WHY: impact starts expanded because economic consequence is a core CA-2 decision input but remains collapsible for review ergonomics. */}
          {impactExpanded && (
            <div id="amendment-impact-content">
              <dl>
                <div>
                  <dt>Affected positions</dt>
                  <dd>{impact.affectedPositionCount ?? 0}</dd>
                </div>
                <div>
                  <dt>Payout before</dt>
                  <dd>{renderEconomicValue(impact, 'payoutBefore')}</dd>
                </div>
                <div>
                  <dt>Payout after</dt>
                  <dd>{renderEconomicValue(impact, 'payoutAfter')}</dd>
                </div>
                <div>
                  <dt>Delta</dt>
                  <dd>{renderEconomicValue(impact, 'payoutDelta')}</dd>
                </div>
              </dl>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                <ProvisionalBadge
                  freshnessTimestamp={impact.freshnessAt}
                  label="Projected"
                  completenessState={impact.completenessState}
                />

                <DataFreshnessIndicator
                  signal="projection"
                  freshnessAt={impact.freshnessAt}
                  status={impact.freshnessStatus}
                />
              </div>

              <PlanVersionCompare
                baseVersion={reviewData.baseVersion}
                compareVersion={reviewData.compareVersion}
                active={compareActive}
                onToggle={setCompareActive}
              />
            </div>
          )}
        </section>

        {/* WHY: mutation failures are rendered immediately above the document-level approval controls so the reviewer knows no governance transition was confirmed. */}
        {error && (
          <div className="modifier-manager-inline-error" role="alert">
            {error}
          </div>
        )}

        <footer className="modifier-manager-footer">
          {/* WHY: router field is callerEligibility not eligibility. ep22bToGateEligibility adapts shape. Corrected S808. */}
          <ApprovalEligibilityGate
            eligibility={ep22bToGateEligibility(reviewData.callerEligibility)}
            blockedMessage="You are not eligible to approve this amendment."
          >
            <button
              type="button"
              className="primary-button"
              disabled={approving}
              onClick={handleApprove}
            >
              {approving ? 'Approving…' : 'Approve amendment'}
            </button>
          </ApprovalEligibilityGate>

          <button
            type="button"
            className="secondary-button"
            disabled={approving}
            onClick={handleReturn}
          >
            Return for Changes
          </button>
        </footer>
      </div>
    </section>
  );
}

// WHY: currentUser removed from PropTypes — not used in function body.
// Component is self-sufficient via the amendment approval-review endpoint callerEligibility.
AmendmentApprovalReview.propTypes = {
  planId: PropTypes.number.isRequired,
  amendmentId: PropTypes.string.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReturn: PropTypes.func.isRequired,
};

export default AmendmentApprovalReview;

/**
 * @example
 * <AmendmentApprovalReview
 *   planId={481}
 *   amendmentId="65309675-40cb-48cc-9f5c-c1bea20ca093"
 *   currentUser={currentUser}
 *   onApprove={() => refreshQueue()}
 *   onReturn={() => setReturnDrawerOpen(true)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - Locked #1/#2: approval is for the complete amended document; no section-level approval action exists.
 * - DP-2: V[N] Current approved and V[N+1] Proposed are always visible together.
 * - INV-1 / DP-1 / DP-9: amendmentEffectiveDate is explicitly labeled economic and no proration is invented.
 * - Locked temporal copy is rendered verbatim: "Approval date does not determine when this amendment governs economic events."
 * - INV-4: temporal context is consumed from the server payload and never derived client-side.
 * - C-59 renders changed values; C-61 renders version comparison.
 * - C-44 and C-46 communicate provisional projection completeness/freshness.
 * - C-45 renders server-authoritative approval eligibility.
 * - XC-6: available, not_available, and partial payout states remain distinct.
 * - DP-10: onApprove fires only after HTTP 200.
 * - XC-7 / DP-14: version and projection states are communicated in text.
 * - DP-8 audit: no sub-document or per-modifier CA-2 approval framing exists.
 */

/**
 * MIGRATION FLAGS — S777
 * - plan.effective_from / plan.effective_to — pending m_tc_01 (Neon FROZEN — 8 migrations pending).
 * - amendmentEffectiveDate — consumed from approval-review payload once endpoint is built.
 * - GET /api/v1/plans/{id}/amendments/{aid}/approval-review — S777 CONFIRMED NOT BUILT.
 *   OI-S774-AMD-APPROVAL-REVIEW-ENDPOINT open. Fetch will 404 until endpoint is implemented.
 *   ep 14 POST /plans/{id}/amendments/{aid}/approve EXISTS — wired correctly (HTTP 200 check, onApprove callback).
 *   ep 15 POST /plans/{id}/amendments/{aid}/return EXISTS — delegated to C-29 via onReturn prop (correct).
 * - VersionDeltaPayload shape — OI-S774-VERSION-DELTA-PAYLOAD open — shape not defined in any spec.
 */

/**
 * ACCESSIBILITY NOTES
 * - Version transition is available as text and through an aria-label, not color alone.
 * - Disclosure controls use aria-expanded and aria-controls.
 * - Economic values use definition-list semantics.
 * - Approval eligibility explanation is delegated to ApprovalEligibilityGate.
 * - Mutation errors use role="alert".
 * - Buttons expose textual loading state while governance mutation is in progress.
 */
